import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { titoloScadenza } from '@praticheenergia/domain'
import { caricaElenco } from '../impianti/impianti.api'
import { Avvisi } from './Avvisi'
import { useDatiQuadro } from './useDatiQuadro'
import { ANNO_IMPOSTA } from '../lib/anno-imposta'
import { Lancetta, Spia, Stato, ZONE_GIORNI } from './Strumenti'
import {
  daFare, dataItaliana, giorniResidui, produzioneImpianto, quadro, riepilogoScadenze, righeDichiarazioniElenco,
  testoResidui, riepilogoDichiarazioni,
} from './quadro'

const MAX_ATTIVITA = 8

const oggiEsteso = () =>
  new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

export function Cruscotto() {
  const idPartner = Number(useParams().idPartner)
  const base = `/cliente/${idPartner}`

  const { data: righe = [] } = useQuery({ queryKey: ['impianti', null], queryFn: () => caricaElenco(null), retry: false })
  const { scadenze, green, soglia, termini, dichiarazioni } = useDatiQuadro(idPartner)

  const righeDich = righeDichiarazioniElenco(dichiarazioni, termini, ANNO_IMPOSTA)
  const q = quadro(scadenze, green, soglia, righeDich)
  const impianti = righe
    .filter((r) => r.stato !== 'archiviato')
    .map((r) => ({ id: r.id, nome: r.codiceImpianto ?? r.codiceDichiarazione, cliente: r.ragioneSociale }))
  const daInviare = dichiarazioni.filter((d) => d.stato !== 'inviata').length
  const attivita = daFare(scadenze, righeDich, green, soglia)
  const giorni = q.prossima && giorniResidui(q.prossima.scadenza)

  return (
    <div data-testid="cruscotto" className="cruscotto">
      <Avvisi />

      <header className="cruscotto-testata">
        <h1>Cruscotto</h1>
        <p>
          {oggiEsteso()} · {impianti.length} impianti
        </p>
      </header>

      <section className="quadro" aria-label="Quadro strumenti">
        <Lancetta
          valore={giorni}
          min={0}
          max={120}
          zone={ZONE_GIORNI}
          testo={giorni === null ? '—' : `${giorni} gg`}
          etichetta="al prossimo adempimento"
          dettaglio={q.prossima ? `${q.prossima.titolo} · ${q.prossima.dove} · ${dataItaliana(q.prossima.scadenza)}` : 'Nessun adempimento in calendario'}
        />
        <div className="quadro-spie">
          <Spia tono={q.scadute > 0 ? 'rosso' : null} icona="avviso" valore={q.scadute} etichetta="Adempimenti scaduti" a={`${base}/scadenzario`} />
          <Spia tono={q.inPreavviso > 0 ? 'ambra' : null} icona="orologio" valore={q.inPreavviso} etichetta="In preavviso" a={`${base}/scadenzario`} />
          <Spia tono={q.sottoSoglia > 0 ? 'rosso' : null} icona="impianti" valore={q.sottoSoglia} etichetta="Produzione sotto soglia" a={`${base}/alert-green`} />
          <Spia tono={q.credenzialiMancanti > 0 ? 'ambra' : null} icona="chiave" valore={q.credenzialiMancanti} etichetta="Credenziali mancanti" a={`${base}/credenziali-distributore`} />
        </div>
        <Lancetta
          valore={q.inRegola}
          min={0}
          max={100}
          zone={[{ fino: 70, tono: 'rosso' }, { fino: 90, tono: 'ambra' }, { fino: 100, tono: 'verde' }]}
          testo={`${q.inRegola}%`}
          etichetta="adempimenti in regola"
          dettaglio={
            <>
              {q.aperte - q.scadute} su {q.aperte} ·{' '}
              <Link to={`${base}/dichiarazioni`}>{daInviare} dichiarazioni da inviare</Link>
            </>
          }
        />
      </section>

      <div className="cruscotto-colonne">
        <section className="riquadro" aria-labelledby="da-fare">
          <h2 id="da-fare">Da fare adesso</h2>
          <p className="riquadro-sottotitolo">Prima le scadute, poi per giorni residui</p>
          {attivita.length === 0 ? (
            <p>Niente in ritardo né in preavviso.</p>
          ) : (
            <ul className="attivita">
              {attivita.slice(0, MAX_ATTIVITA).map((a) => (
                <li key={a.chiave} className={`attivita-${a.tono}`}>
                  <span className="attivita-giorni">
                    {a.giorni === null ? '!' : a.giorni}
                    {a.giorni !== null && <small>giorni</small>}
                  </span>
                  <span className="attivita-testo">
                    <strong>{a.titolo}</strong>
                    <span>{a.dettaglio}</span>
                  </span>
                  <Link to={`${base}/${a.rotta}`}>{a.azione}</Link>
                </li>
              ))}
            </ul>
          )}
          {attivita.length > MAX_ATTIVITA && (
            <Link to={`${base}/scadenzario`}>Altre {attivita.length - MAX_ATTIVITA} nello scadenzario</Link>
          )}
        </section>

        <section className="riquadro" aria-labelledby="stato-impianti">
          <h2 id="stato-impianti">Stato degli impianti</h2>
          <p className="riquadro-sottotitolo">Una spia per area: apri l'impianto per il suo cruscotto</p>
          <div className="tabella-scorre">
            <table className="tabella-quadro">
              <thead>
                <tr>
                  <th>Impianto</th>
                  <th>Prossimo adempimento</th>
                  <th>Scadenze</th>
                  <th>Produzione</th>
                  <th>Dichiarazioni</th>
                </tr>
              </thead>
              <tbody>
                {impianti.map((i) => {
                  const r = riepilogoScadenze(scadenze.filter((s) => s.impiantoId === i.id))
                  const p = produzioneImpianto(green.find((g) => g.impiantoId === i.id), soglia)
                  const d = riepilogoDichiarazioni(dichiarazioni.filter((x) => x.impiantoId === i.id), termini, ANNO_IMPOSTA)
                  return (
                    <tr key={i.id}>
                      <td>
                        <Link to={`${base}/impianti/${i.id}`}>{i.nome}</Link>
                        <small>{i.cliente}</small>
                      </td>
                      <td>
                        {r.prossima ? titoloScadenza(r.prossima) : '—'}
                        {r.prossima && <small>{testoResidui(giorniResidui(r.prossima.scadenza))}</small>}
                      </td>
                      <td><Stato tono={r.tono}>{r.testo}</Stato></td>
                      <td><Stato tono={p.tono}>{p.testo}</Stato></td>
                      <td><Stato tono={d.tono}>{d.testo}</Stato></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  )
}
